import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash, ShieldWarning, ShieldCheck, Warning, CloudArrowDown } from "@phosphor-icons/react";

const STATUS_COLORS = { verde: "#10B981", amarillo: "#F59E0B", rojo: "#EF4444", na: "#A1A1AA" };
const STATUS_LABELS = { verde: "Vigente", amarillo: "Vence pronto", rojo: "Vencido", na: "Sin vencimiento" };

const fileToB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]); // strip "data:...;base64,"
    r.onerror = rej;
    r.readAsDataURL(file);
});

export default function TechnicianDocuments() {
    const { cedula } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [types, setTypes] = useState([]);
    const [showUpload, setShowUpload] = useState(false);
    const isOwnTechnician = user?.role === "tecnico" && user?.cedula === cedula;
    const canManage = user?.role === "supervisor" || isOwnTechnician;

    const load = async () => {
        const [d, t] = await Promise.all([
            api.get(`/technicians/${cedula}/documents`),
            api.get("/document-types"),
        ]);
        setData(d.data); setTypes(t.data);
    };
    useEffect(() => { load(); }, [cedula]);

    const del = async (doc_id) => {
        if (!window.confirm("¿Eliminar este documento del expediente?")) return;
        await api.delete(`/technicians/${cedula}/documents/${doc_id}`);
        load();
    };

    const download = async (doc_id, filename) => {
        const r = await api.get(`/technicians/${cedula}/documents/${doc_id}/file`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = filename || "documento.pdf"; a.click();
        URL.revokeObjectURL(url);
    };

    if (!data) return <div className="p-8 text-zinc-500">Cargando…</div>;
    const t = data.technician;
    const docs = data.documents;
    const summary = data.summary;

    // Group docs by type code (most recent first)
    const byType = {};
    for (const d of docs) {
        if (!byType[d.tipo]) byType[d.tipo] = [];
        byType[d.tipo].push(d);
    }

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8" data-testid="tech-docs-page">
            <Link to="/technicians" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="back-techs">
                <ArrowLeft size={14} /> Volver a técnicos
            </Link>

            <div className="border-b border-zinc-200 pb-6 mb-8 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="overline mb-2" style={{ color: "#0F766E" }}>Expediente HSE · ISO 45001:2018</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter">{t?.nombre || cedula}</h1>
                    <div className="font-mono text-xs text-zinc-500 mt-1">CC {cedula} · {t?.especialidad || "Sin especialidad"}</div>
                </div>
                {canManage && (
                    <button onClick={() => setShowUpload(s => !s)} className="btn-accent flex items-center gap-2" data-testid="toggle-upload">
                        <Plus size={16} weight="bold" /> {showUpload ? "Cancelar" : "Cargar documento"}
                    </button>
                )}
            </div>

            {/* Resumen */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-zinc-200 mb-8" data-testid="hse-summary">
                <Stat label="Documentos cargados" value={summary.total} />
                <Stat label="Críticos vencidos" value={summary.criticos_vencidos} highlight={summary.criticos_vencidos > 0} color={summary.criticos_vencidos > 0 ? "#EF4444" : null} />
                <Stat label="Por vencer (≤30d)" value={summary.por_vencer} color={summary.por_vencer > 0 ? "#F59E0B" : null} />
                <Stat label="Estado general" value={summary.criticos_vencidos > 0 ? "BLOQUEADO" : (summary.por_vencer > 0 ? "ALERTA" : "OK")} color={summary.criticos_vencidos > 0 ? "#EF4444" : (summary.por_vencer > 0 ? "#F59E0B" : "#10B981")} />
            </div>

            {summary.criticos_vencidos > 0 && (
                <div className="p-4 border border-red-300 bg-red-50 text-sm text-red-900 mb-6 flex items-start gap-3" data-testid="hse-blocked-alert">
                    <ShieldWarning size={20} weight="fill" />
                    <div>
                        <strong>Técnico bloqueado para agendamiento.</strong> Hay documentos críticos vencidos o faltantes. {canManage ? "Carga/renueva los documentos para habilitarlo." : "Avísale al supervisor para que lo actualice."}
                    </div>
                </div>
            )}

            {showUpload && canManage && (
                <UploadForm types={types} onDone={() => { setShowUpload(false); load(); }} onCancel={() => setShowUpload(false)} cedula={cedula} />
            )}

            <div className="overline mb-3 mt-4">Documentos por tipo</div>
            <div className="space-y-3">
                {types.map(tp => {
                    const ds = byType[tp.code] || [];
                    const latest = ds[0];
                    const isMissing = !latest;
                    const isCritical = tp.critico;
                    return (
                        <div key={tp.code} className="border border-zinc-200 p-4" data-testid={`doc-type-${tp.code}`}>
                            <div className="flex items-start justify-between flex-wrap gap-3">
                                <div className="flex items-start gap-3">
                                    {isCritical
                                        ? <ShieldCheck size={20} weight="fill" style={{ color: latest ? STATUS_COLORS[latest.status] : "#A1A1AA" }} />
                                        : <Warning size={18} style={{ color: "#71717A" }} />}
                                    <div>
                                        <div className="font-semibold text-sm">{tp.label} {isCritical && <span className="ml-1 text-[10px] uppercase font-mono" style={{ color: "#EF4444" }}>· crítico</span>} {tp.optional && <span className="ml-1 text-[10px] uppercase font-mono text-zinc-400">· opcional</span>}</div>
                                        <div className="text-xs text-zinc-500 mt-0.5">{ds.length} {ds.length === 1 ? "versión" : "versiones"}</div>
                                    </div>
                                </div>
                                {isMissing
                                    ? (isCritical
                                        ? <span className="badge-sev sev-rojo">SIN CARGAR</span>
                                        : <span className="badge-sev sev-amarillo">SIN CARGAR</span>)
                                    : <span className="badge-sev" style={{ background: STATUS_COLORS[latest.status] + "20", color: STATUS_COLORS[latest.status], borderColor: STATUS_COLORS[latest.status] }}>{STATUS_LABELS[latest.status]}</span>}
                            </div>
                            {ds.length > 0 && (
                                <div className="mt-3 border-t border-zinc-100 pt-3 space-y-2">
                                    {ds.map(d => (
                                        <div key={d.doc_id} className="flex items-center justify-between text-xs flex-wrap gap-2" data-testid={`doc-row-${d.doc_id}`}>
                                            <div className="flex items-center gap-3">
                                                <div className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[d.status] }} />
                                                <span className="font-mono">{d.filename}</span>
                                                {d.fecha_vencimiento && <span className="text-zinc-500">vence {d.fecha_vencimiento}</span>}
                                                {d.entidad_emisora && <span className="text-zinc-500">· {d.entidad_emisora}</span>}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => download(d.doc_id, d.filename)} className="text-xs font-semibold flex items-center gap-1" style={{ color: "#0F766E" }} data-testid={`download-${d.doc_id}`}>
                                                    <CloudArrowDown size={14} /> Descargar
                                                </button>
                                                {canManage && (
                                                    <button onClick={() => del(d.doc_id)} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1" data-testid={`delete-${d.doc_id}`}>
                                                        <Trash size={12} /> Eliminar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function Stat({ label, value, highlight, color }) {
    return (
        <div className={`p-5 border-r border-zinc-200 last:border-r-0 ${highlight ? "bg-red-50" : ""}`}>
            <div className="overline mb-2">{label}</div>
            <div className="font-display text-2xl font-black tracking-tighter" style={{ color: color || "#0a0a0a" }}>{value}</div>
        </div>
    );
}

function UploadForm({ types, cedula, onDone, onCancel }) {
    const [form, setForm] = useState({ tipo: "eps", filename: "", file_base64: "", fecha_emision: "", fecha_vencimiento: "", numero: "", entidad_emisora: "", observaciones: "" });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const meta = types.find(t => t.code === form.tipo);
    const reqVenc = meta?.requires_vencimiento;

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) { setError("Archivo demasiado grande (máx 5MB)"); return; }
        const b64 = await fileToB64(f);
        setForm(s => ({ ...s, filename: f.name, file_base64: b64, mime_type: f.type || "application/pdf" }));
        setError("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmitting(true);
        if (!form.file_base64) { setError("Selecciona el archivo"); setSubmitting(false); return; }
        if (reqVenc && !form.fecha_vencimiento) { setError("Este tipo requiere fecha de vencimiento"); setSubmitting(false); return; }
        try {
            await api.post(`/technicians/${cedula}/documents`, form);
            onDone();
        } catch (err) {
            setError(err.response?.data?.detail || "Error al subir");
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="border border-teal-200 bg-teal-50 p-6 mb-8 grid md:grid-cols-2 gap-4" data-testid="upload-form">
            <div className="md:col-span-2">
                <div className="overline mb-1" style={{ color: "#0F766E" }}>Cargar documento HSE</div>
                <h3 className="font-display text-xl font-bold tracking-tight">Nuevo soporte de cumplimiento</h3>
            </div>
            <label><div className="overline mb-1.5">Tipo de documento *</div>
                <select className="input-atia" value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})} required data-testid="up-tipo">
                    {types.map(t => <option key={t.code} value={t.code}>{t.label}{t.critico ? " (crítico)" : ""}{t.optional ? " (opcional)" : ""}</option>)}
                </select>
            </label>
            <label><div className="overline mb-1.5">Archivo (PDF/imagen, máx 5MB) *</div>
                <input type="file" accept="application/pdf,image/*" onChange={handleFile} className="input-atia" required data-testid="up-file" />
                {form.filename && <div className="text-xs text-zinc-600 mt-1">📄 {form.filename}</div>}
            </label>
            <label><div className="overline mb-1.5">Fecha emisión</div>
                <input type="date" className="input-atia" value={form.fecha_emision} onChange={e => setForm({...form, fecha_emision: e.target.value})} data-testid="up-emision" />
            </label>
            <label><div className="overline mb-1.5">Fecha vencimiento {reqVenc && <span className="text-red-500">*</span>}</div>
                <input type="date" className="input-atia" value={form.fecha_vencimiento} onChange={e => setForm({...form, fecha_vencimiento: e.target.value})} data-testid="up-venc" required={reqVenc} />
            </label>
            <label><div className="overline mb-1.5">Entidad emisora</div>
                <input className="input-atia" value={form.entidad_emisora} onChange={e => setForm({...form, entidad_emisora: e.target.value})} data-testid="up-entidad" placeholder="Ej: SURA, ARL Positiva, SENA" />
            </label>
            <label><div className="overline mb-1.5">Número / Folio</div>
                <input className="input-atia" value={form.numero} onChange={e => setForm({...form, numero: e.target.value})} data-testid="up-numero" />
            </label>
            <label className="md:col-span-2"><div className="overline mb-1.5">Observaciones</div>
                <textarea rows={2} className="input-atia" value={form.observaciones} onChange={e => setForm({...form, observaciones: e.target.value})} data-testid="up-obs" />
            </label>
            {error && <div className="md:col-span-2 p-3 border border-red-300 bg-red-50 text-red-900 text-sm" data-testid="up-error">{error}</div>}
            <div className="md:col-span-2 flex justify-end gap-3">
                <button type="button" onClick={onCancel} className="btn-secondary" data-testid="up-cancel">Cancelar</button>
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="up-submit">{submitting ? "Guardando…" : "Guardar documento →"}</button>
            </div>
        </form>
    );
}
